import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import ModalNovoMdfe from './ModalNovoMdfe';
import ModalEncerrarMdfe from './ModalEncerrarMdfe';
import { abrirDanfeNfePdf } from '../../utils/impressaoVenda'; // genérico apesar do nome — só abre um endpoint que devolve PDF
import pedirSenhaAdmin from '../common/pedirSenhaAdmin';
import s from '../../pages/Financeiro.module.css';

const fmtMoeda = v => v == null ? '—' : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtDT    = d => d ? new Date(d).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—';

const STATUS_LABEL = {
  digitacao: 'Em digitação', validado: 'Validado', autorizado: 'Autorizado',
  rejeitado: 'Rejeitado', cancelado: 'Cancelado', encerrado: 'Encerrado',
};
const STATUS_BADGE = {
  digitacao: s.badgeOutros, validado: s.badgeCaixa, autorizado: s.badgeAtivo,
  rejeitado: s.badgeInativo, cancelado: s.badgeOutros, encerrado: s.badgeBanco,
};

// Tela principal do MDF-e — listagem + entrada pro fluxo de montagem
// (ModalNovoMdfe/ModalEncerrarMdfe). Etapa 11: alerta de MDF-e autorizado
// aberto (dois níveis — ver abaixo). O resto (Editar/Validar/Gerar XML/
// Transmitir/Consultar SEFAZ/DAMDFE/Cancelar/Encerrar) já funciona de
// ponta a ponta.
export default function AbaMdfe() {
  const [lista, setLista] = useState([]);
  const [status, setStatus] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [info, setInfo] = useState(''); // feedback neutro (ex.: resultado de "Consultar SEFAZ")
  const [modalAberto, setModalAberto] = useState(null); // null | 'novo' | id
  const [validandoId, setValidandoId] = useState(null);
  const [naoEncerrados, setNaoEncerrados] = useState(null); // null = ainda não consultou; [] | [...] depois da consulta real
  const [consultandoNaoEnc, setConsultandoNaoEnc] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const { data } = await api.get('/fiscal/mdfe', { params: { status: status || undefined } });
      setLista(data || []);
    } catch { setErro('Erro ao carregar MDF-e.'); }
    finally { setCarregando(false); }
  }, [status]);

  useEffect(() => { carregar(); }, [carregar]);

  const excluir = async (m) => {
    if (!window.confirm(`Excluir o MDF-e ${m.numero ? `nº ${m.numero}` : '(rascunho)'}?`)) return;
    setErro('');
    try {
      await api.delete(`/fiscal/mdfe/${m.id}`);
      await carregar();
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao excluir.');
    }
  };

  const validarRapido = async (m) => {
    setValidandoId(m.id); setErro('');
    try {
      const { data } = await api.post(`/fiscal/mdfe/${m.id}/validar`);
      if (!data.ok) setErro(`MDF-e nº ${m.numero || m.id}: ${data.problemas.join(' ')}`);
      await carregar();
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao validar.');
    } finally { setValidandoId(null); }
  };

  // Etapa 4: monta + assina + valida contra o XSD oficial. Ainda NÃO
  // transmite (isso é Etapa 6/7) — só baixa o XML pra conferência, e o
  // resultado da validação aparece na hora (sem esconder erro nenhum).
  const [gerandoId, setGerandoId] = useState(null);
  const gerarXml = async (m) => {
    setGerandoId(m.id); setErro('');
    try {
      const { data } = await api.post(`/fiscal/mdfe/${m.id}/gerar-xml`);
      if (!data.valido) {
        setErro(`XML do MDF-e nº ${m.numero || m.id} gerado, mas com pendência(s) no schema: ${data.erros.join(' ')}`);
      } else {
        const blob = new Blob([data.xml], { type: 'application/xml' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `MDFe${data.chave}.xml`; a.click();
        URL.revokeObjectURL(url);
      }
      await carregar();
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao gerar o XML.');
    } finally { setGerandoId(null); }
  };

  // Etapa 7: transmite de verdade — ação irreversível (autorização na
  // SEFAZ), por isso pede confirmação antes. Remonta/assina/valida na hora
  // (mesmo caminho de "Gerar XML"), então funciona mesmo sem ter clicado
  // em "Gerar XML" antes.
  const [transmitindoId, setTransmitindoId] = useState(null);
  const transmitir = async (m) => {
    if (!window.confirm(`Transmitir o MDF-e ${m.numero ? `nº ${m.numero}` : ''} pra SEFAZ? Depois de autorizado, só dá pra cancelar/encerrar (não editar).`)) return;
    setTransmitindoId(m.id); setErro('');
    try {
      const { data } = await api.post(`/fiscal/mdfe/${m.id}/transmitir`);
      if (!data.homologado) {
        setErro(`Rejeição ${data.cStat} – ${data.xMotivo}`);
      }
      await carregar();
    } catch (err) {
      const d = err.response?.data;
      setErro(d?.erros ? `${d.erro} ${d.erros.join(' ')}` : (d?.erro || 'Erro ao transmitir.'));
    } finally { setTransmitindoId(null); }
  };

  const [consultandoId, setConsultandoId] = useState(null);
  const consultarSefaz = async (m) => {
    setConsultandoId(m.id); setErro(''); setInfo('');
    try {
      const { data } = await api.post(`/fiscal/mdfe/${m.id}/consultar-sefaz`);
      setInfo(`SEFAZ: ${data.cStat} – ${data.xMotivo}`);
      await carregar();
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao consultar a SEFAZ.');
    } finally { setConsultandoId(null); }
  };

  // Etapa 9: cancelamento (evento 110111). Justificativa + senha de admin —
  // mesmo padrão de cancelar NF-e/NFC-e e de inutilizar numeração
  // (ModuloFiscal.jsx). Prazo de 24h da autorização é checado no backend
  // (services/mdfe/eventoCancelamento.js) — aqui só repassa o erro amigável
  // se estourou o prazo.
  const [cancelandoId, setCancelandoId] = useState(null);
  const cancelar = async (m) => {
    const justificativa = window.prompt('Justificativa do cancelamento (mínimo 15 caracteres):', '');
    if (justificativa === null) return;
    if (justificativa.trim().length < 15) { setErro('A justificativa deve ter no mínimo 15 caracteres.'); return; }

    const senha = await pedirSenhaAdmin(
      `Cancelar o MDF-e ${m.numero ? `nº ${m.numero}` : ''} na SEFAZ. Só é permitido em até 24h da autorização, e a operação é IRREVERSÍVEL.`,
      { acao: 'cancelar_mdfe', detalhes: `MDF-e chave ${m.chave_acesso}` }
    );
    if (!senha) return;

    setCancelandoId(m.id); setErro(''); setInfo('');
    try {
      const { data } = await api.post(`/fiscal/mdfe/${m.id}/cancelar`, { justificativa: justificativa.trim(), senha_admin: senha });
      if (!data.homologado) setErro(`Rejeição ${data.cStat} – ${data.xMotivo}`);
      else setInfo(`MDF-e cancelado — protocolo ${data.protocolo}.`);
      await carregar();
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao cancelar.');
    } finally { setCancelandoId(null); }
  };

  // Etapa 10 ("atenção especial") — pede UF/município de encerramento (o
  // ModalEncerrarMdfe cuida do formulário + senha de admin, aqui só trata
  // o resultado).
  const [mdfeEncerrando, setMdfeEncerrando] = useState(null);
  const aoEncerrar = (data) => {
    setMdfeEncerrando(null); setErro(''); setInfo('');
    if (!data.homologado) setErro(`Rejeição ${data.cStat} – ${data.xMotivo}`);
    else setInfo(`MDF-e encerrado — protocolo ${data.protocolo}.`);
    carregar();
  };

  // Etapa 11 — dois níveis de alerta, de propósito:
  // 1) contagem local (instantânea, sem rede): quantos MDF-e este cadastro
  //    tem como "autorizado" aqui dentro. É só um resumo do que já está na
  //    tela — não é a fonte de verdade sobre o que a SEFAZ considera aberto.
  // 2) consulta real MDFeConsNaoEnc, sob demanda (botão): a SEFAZ não
  //    define um prazo fixo pra encerrar, então nenhuma conta local (tipo
  //    "autorizado há mais de N dias") substituiria perguntar pra ela.
  const autorizadosLocal = lista.filter(m => m.status === 'autorizado').length;

  const verificarNaoEncerrados = async () => {
    setConsultandoNaoEnc(true); setErro(''); setInfo('');
    try {
      const { data } = await api.get('/fiscal/mdfe/nao-encerrados');
      setNaoEncerrados(data.mdfesNaoEncerrados || []);
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao consultar MDF-e não encerrados na SEFAZ.');
    } finally { setConsultandoNaoEnc(false); }
  };

  return (
    <div>
      <div className={s.toolbar} style={{ flexWrap: 'wrap' }}>
        <button className={s.btnErpPrimario} onClick={() => setModalAberto('novo')}>+ Novo MDF-e</button>
        <select className={s.select} value={status} onChange={e => setStatus(e.target.value)}>
          <option value="">Todos os status</option>
          {Object.entries(STATUS_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <button className={s.btnErp} disabled={consultandoNaoEnc} title="Pergunta pra SEFAZ (MDFeConsNaoEnc) quais MDF-e deste CNPJ ainda estão abertos"
          onClick={verificarNaoEncerrados}>
          {consultandoNaoEnc ? '...' : '🔔 Verificar não encerrados na SEFAZ'}
        </button>
        <span style={{ fontSize: 11, color: '#666' }}>Modal rodoviário.</span>
      </div>

      {autorizadosLocal > 0 && (
        <div className={s.sucessoMsg} style={{ background: '#fef9c3', color: '#854d0e' }}>
          {autorizadosLocal} MDF-e autorizado{autorizadosLocal > 1 ? 's' : ''} aguardando encerramento (viagem em andamento ou pendente de finalizar).
        </div>
      )}
      {naoEncerrados && naoEncerrados.length === 0 && (
        <div className={s.sucessoMsg}>SEFAZ confirma: nenhum MDF-e não encerrado para este CNPJ.</div>
      )}
      {naoEncerrados && naoEncerrados.length > 0 && (
        <div className={s.erroMsg}>
          A SEFAZ (MDFeConsNaoEnc) aponta {naoEncerrados.length} MDF-e não encerrado{naoEncerrados.length > 1 ? 's' : ''}:{' '}
          {naoEncerrados.map(n => n.numero ? `nº ${n.numero}` : `chave ${n.chave}`).join(', ')}.
        </div>
      )}

      {erro && <div className={s.erroMsg}>{erro}</div>}
      {info && <div className={s.sucessoMsg}>{info}</div>}

      <table className={s.tabela}>
        <thead><tr>
          <th style={{ width: 70 }}>Número</th>
          <th style={{ width: 110 }}>Emissão</th>
          <th>Veículo</th>
          <th>Origem</th>
          <th>Destino</th>
          <th style={{ width: 70, textAlign: 'center' }}>Docs.</th>
          <th style={{ width: 100, textAlign: 'right' }}>Peso (kg)</th>
          <th style={{ width: 110 }}>Situação</th>
          <th style={{ width: 190 }}>Ações</th>
        </tr></thead>
        <tbody>
          {carregando && <tr><td colSpan={9} style={{ textAlign: 'center', color: '#888', padding: 14 }}>Carregando...</td></tr>}
          {!carregando && lista.length === 0 && (
            <tr><td colSpan={9} style={{ textAlign: 'center', color: '#888', padding: 14 }}>Nenhum MDF-e encontrado.</td></tr>
          )}
          {lista.map(m => (
            <tr key={m.id}>
              <td>{m.numero || <em style={{ color: '#888' }}>rascunho</em>}</td>
              <td>{fmtDT(m.data_emissao)}</td>
              <td>{m.veiculo_placa || '—'}</td>
              <td>{m.municipio_carregamento_nome || '—'}/{m.uf_carregamento}</td>
              <td>{m.municipios_descarga || '—'}/{m.uf_descarregamento}</td>
              <td style={{ textAlign: 'center' }}>{m.qtd_documentos}</td>
              <td style={{ textAlign: 'right' }}>{Number(m.peso_total_kg || 0).toLocaleString('pt-BR')}</td>
              <td>
                <span className={`${s.badge} ${STATUS_BADGE[m.status] || s.badgeOutros}`}
                  title={m.status === 'rejeitado' ? m.motivo_rejeicao : ''}>
                  {STATUS_LABEL[m.status] || m.status}
                </span>
                {m.status === 'rejeitado' && m.motivo_rejeicao && (
                  <div style={{ fontSize: 10, color: '#b91c1c', maxWidth: 160 }}>{m.motivo_rejeicao}</div>
                )}
              </td>
              <td>
                {['digitacao', 'rejeitado', 'validado'].includes(m.status) && (
                  <>
                    <button className={s.btnErp} style={{ height: 22, fontSize: 11, padding: '0 6px', marginRight: 4 }}
                      onClick={() => setModalAberto(m.id)}>✏️ Editar</button>
                    <button className={s.btnErp} style={{ height: 22, fontSize: 11, padding: '0 6px', marginRight: 4 }}
                      disabled={validandoId === m.id} onClick={() => validarRapido(m)}>
                      {validandoId === m.id ? '...' : '✔ Validar'}
                    </button>
                    <button className={s.btnErp} style={{ height: 22, fontSize: 11, padding: '0 6px', marginRight: 4 }}
                      disabled={gerandoId === m.id} title="Monta, assina e valida o XML contra o schema oficial (não transmite à SEFAZ ainda)"
                      onClick={() => gerarXml(m)}>
                      {gerandoId === m.id ? '...' : '📄 Gerar XML'}
                    </button>
                    <button className={s.btnErpPrimario} style={{ height: 22, fontSize: 11, padding: '0 6px', marginRight: 4 }}
                      disabled={transmitindoId === m.id} title="Transmite de verdade pra SEFAZ — ação irreversível"
                      onClick={() => transmitir(m)}>
                      {transmitindoId === m.id ? '...' : '🚀 Transmitir'}
                    </button>
                    <button className={s.btnErp} style={{ height: 22, fontSize: 11, padding: '0 6px', color: '#b91c1c' }}
                      onClick={() => excluir(m)}>🗑</button>
                  </>
                )}
                {['autorizado', 'rejeitado'].includes(m.status) && m.chave_acesso && (
                  <button className={s.btnErp} style={{ height: 22, fontSize: 11, padding: '0 6px', marginRight: 4 }}
                    disabled={consultandoId === m.id} title="Pergunta pra SEFAZ a situação atual dessa chave"
                    onClick={() => consultarSefaz(m)}>
                    {consultandoId === m.id ? '...' : '🔍 Consultar SEFAZ'}
                  </button>
                )}
                {['autorizado', 'cancelado', 'encerrado'].includes(m.status) && (
                  <button className={s.btnErp} style={{ height: 22, fontSize: 11, padding: '0 6px', marginRight: 4 }}
                    onClick={() => abrirDanfeNfePdf(`/fiscal/mdfe/${m.id}/damdfe`)}>
                    🖨️ DAMDFE
                  </button>
                )}
                {m.status === 'autorizado' && (
                  <button className={s.btnErp} style={{ height: 22, fontSize: 11, padding: '0 6px', marginRight: 4, color: '#b91c1c' }}
                    disabled={cancelandoId === m.id} title="Cancela o MDF-e na SEFAZ — só até 24h da autorização"
                    onClick={() => cancelar(m)}>
                    {cancelandoId === m.id ? '...' : '❌ Cancelar'}
                  </button>
                )}
                {m.status === 'autorizado' && (
                  <button className={s.btnErp} style={{ height: 22, fontSize: 11, padding: '0 6px', marginRight: 4 }}
                    title="Encerra o MDF-e — viagem concluída"
                    onClick={() => setMdfeEncerrando(m)}>
                    🏁 Encerrar
                  </button>
                )}
                {!['digitacao', 'rejeitado', 'validado'].includes(m.status) && (
                  <button className={s.btnErp} style={{ height: 22, fontSize: 11, padding: '0 6px' }}
                    onClick={() => setModalAberto(m.id)}>👁 Visualizar</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {modalAberto && (
        <ModalNovoMdfe
          mdfeId={modalAberto === 'novo' ? null : modalAberto}
          onClose={() => setModalAberto(null)}
          onSalvo={() => { setModalAberto(null); carregar(); }}
        />
      )}
      {mdfeEncerrando && (
        <ModalEncerrarMdfe
          mdfe={mdfeEncerrando}
          onClose={() => setMdfeEncerrando(null)}
          onEncerrado={aoEncerrar}
        />
      )}
    </div>
  );
}
